import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import AlertDialog from "@/components/AlertDialog";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import SearchBox from "@/components/SearchBox";
import useDebounce from "@/hooks/useDebounce";
import { useActions, useActionDeleteOne } from "@/hooks/useActions";
import { ActionStatusEnum } from "@/domain/action.enum";
import Select from "@/components/Select";
import { Action } from "@/domain/action.interface";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import { ActionModal } from "@/ui/modals/actions/ActionModal";
import ActionMenu from "@/components/ActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";

interface Filters {
  sortField: string;
  sortOrder: number;
  search: string;
  pageNumber: number;
  pageSize: number;
  status?: string[];
}

const ActionsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t: tCommon } = useTranslation("common");

  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();

  const { alertState, closeAlert, confirmAction } = useAlertModal();

  const createAuditLog = useCreateAuditlog();
  const deleteActionOne = useActionDeleteOne();

  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle("Actions");
  }, []);

  const statusOptions = useMemo(() => {
    return Object.values(ActionStatusEnum).reduce(
      (acc, status) => {
        acc[status] = tCommon(`enums.${status}`);
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [tCommon]);

  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    action: Action | null;
  }>({
    open: false,
    action: null,
  });

  const [filters, setFilters] = useState<Filters>({
    sortField: "name",
    sortOrder: 1,
    search: "",
    pageNumber: 0,
    pageSize: 10,
    status: [ActionStatusEnum.ACTIVE], // Default to ACTIVE
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const { actions, totalCount, isLoadingActions, refetchActions } = useActions({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: filters.search,
    status: filters.status,
  });

  const rows = useMemo(() => {
    if (!actions || actions.length === 0) return [];

    return actions.map((action, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedAction(action);
            setModalMode("edit");
            setIsActionModalOpen(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setShowDeleteModal({ open: true, action });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const row = createDataRow(action._id || index, {
        name: <Typography variant="bodySmall">{action.name}</Typography>,
        code: <Typography variant="bodyMedium">{action.code}</Typography>,
        type: <Typography variant="bodyMedium">{action.type}</Typography>,
        status: (
          <Chip
            sx={{
              bgcolor:
                action.status === ActionStatusEnum.ACTIVE
                  ? theme.palette.brand.light
                  : theme.palette.grey[300],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                action.status === ActionStatusEnum.ACTIVE
                  ? theme.palette.brand.main
                  : theme.palette.grey[500],
            }}
            label={
              <Typography variant="labelSmall">
                {statusOptions[action.status] || action.status}
              </Typography>
            }
            size="medium"
          />
        ),
        actions: <ActionMenu id={action._id} options={actionMenu} />,
      });

      return row;
    });
  }, [actions, statusOptions, theme]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: false,
      label: "Name",
      align: "left",
    },
    {
      id: "code",
      isSortable: true,
      disablePadding: false,
      label: "Code",
      align: "left",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
    },
  ];

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ACTIONS,
        option: OPTIONS.VIEW,
        actionCode: ACTION_CODE.WEB_ACTIONS_VIEW,
        action: "When entering",
        detail: "Viewed the list of actions",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  const previousSearchRef = React.useRef<string>("");
  useEffect(() => {
    if (userInfo) {
      if (
        searchDebounce !== previousSearchRef.current &&
        searchDebounce &&
        searchDebounce.trim().length > 0
      ) {
        previousSearchRef.current = searchDebounce;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.SEARCH,
          actionCode: ACTION_CODE.WEB_ACTIONS_SEARCH,
          action: "When searching",
          detail: "Searched within the list of actions",
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }
    }
  }, [userInfo, searchDebounce, createAuditLog]);

  const handleDelete = async () => {
    if (!showDeleteModal.action) return;

    const actionName = showDeleteModal.action.name;

    deleteActionOne.mutate(showDeleteModal.action._id, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.ACTIONS,
          option: OPTIONS.DELETE,
          actionCode: ACTION_CODE.WEB_ACTIONS_DELETE,
          action: "When deleting",
          detail: `Delete action ${actionName}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });

        refetchActions();
        setShowDeleteModal({ open: false, action: null });
        showSnackBar("Action deleted successfully", "success");
      },
      onError: (error: any) => {
        setShowDeleteModal({ open: false, action: null });
        showSnackBar(
          error?.response?.data?.message || "Failed to delete action",
          "error",
        );
      },
    });
  };

  return (
    <Box>
      {/* Top Bar - Header with title and buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
        }}
      >
        <Typography variant="headlineMedium">Actions</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isLoadingActions ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedAction(null);
                  setModalMode("create");
                  setIsActionModalOpen(true);
                }}
              >
                ADD
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search on left, status dropdown on right */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 50%" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 calc(50% - 5px)" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    status: Array.isArray(value) ? value : [value],
                    pageNumber: 0,
                  }));
                }}
                data={[
                  {
                    key: ActionStatusEnum.ACTIVE,
                    value: tCommon(`enums.${ActionStatusEnum.ACTIVE}`),
                  },
                  {
                    key: ActionStatusEnum.INACTIVE,
                    value: tCommon(`enums.${ActionStatusEnum.INACTIVE}`),
                  },
                ]}
                value={filters.status || []}
                label="Status"
                id="actions-status"
                width="100%"
                multiple
              />
            </Box>
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={totalCount}
            isLoading={isLoadingActions}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
            messageEmptyData="No items to show"
          />
        </CardContent>
      </Card>

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title="Delete Action"
          description="Are you sure you want to delete this action?"
          onCancel={() => {
            setShowDeleteModal({ open: false, action: null });
          }}
          onConfirm={handleDelete}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}

      {/* Action Modal */}
      <ActionModal
        open={isActionModalOpen}
        mode={modalMode}
        action={selectedAction}
        onClose={() => {
          setIsActionModalOpen(false);
          setSelectedAction(null);
        }}
        onSuccess={() => {
          refetchActions();
        }}
        canAddAction={true}
        canEditAction={true}
      />
    </Box>
  );
};

export default ActionsPage;
